import React, { createContext, useContext, useState, useEffect } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("gifted_token");
    const stored = localStorage.getItem("gifted_user");
    if (token && stored) {
      setUser(JSON.parse(stored));
      api
        .get("/auth/me")
        .then((res) => {
          setUser((u) => ({ ...u, ...res.data }));
        })
        .catch(() => {
          localStorage.removeItem("gifted_token");
          localStorage.removeItem("gifted_user");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const persist = (token, u) => {
    localStorage.setItem("gifted_token", token);
    localStorage.setItem("gifted_user", JSON.stringify(u));
    setUser(u);
  };

  // /auth/login, /auth/register and /auth/google all return only a minimal
  // {id, name, email} user shape. Fetch the full profile (onboarding_completed,
  // is_root_admin, role, subscription_tier, etc.) right away so the Founder's Badge and the
  // onboarding overlay reflect true DB state immediately — not just after a full page reload.
  const persistFull = async (token, minimalUser) => {
    persist(token, minimalUser);
    try {
      const me = await api.get("/auth/me");
      const merged = { ...minimalUser, ...me.data };
      persist(token, merged);
      return merged;
    } catch (e) {
      console.error("Could not fetch full profile after auth:", e);
      return minimalUser;
    }
  };

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    return await persistFull(res.data.token, res.data.user);
  };

  const register = async (name, email, password) => {
    let guest_tokens;
    try {
      const raw = localStorage.getItem("gifted_guest_tokens");
      if (raw) guest_tokens = JSON.parse(raw);
    } catch (e) { console.error("Could not parse stored guest tokens:", e); }
    const res = await api.post("/auth/register", { name, email, password, guest_tokens });
    localStorage.removeItem("gifted_guest_tokens");
    return await persistFull(res.data.token, res.data.user);
  };

  const loginWithGoogle = async (credential) => {
    const res = await api.post("/auth/google", { credential });
    return await persistFull(res.data.token, res.data.user);
  };

  const loginWithApple = async (idToken, name) => {
    const res = await api.post("/auth/apple", { id_token: idToken, name });
    return await persistFull(res.data.token, res.data.user);
  };

  // One-Tap Sovereign Auth — completes the real Sign in with Apple redirect flow. The backend
  // (/api/auth/apple/callback) already verified the identity token, ran the Sovereign ID
  // bridge, and minted a session token; we just need to adopt it — no password screen, no
  // extra round-trip. Lands the user straight in the Maison Studio. Validates the token via
  // /auth/me FIRST (throwing on failure) so the caller can show a clear error instead of
  // silently persisting a dead token and bouncing through a Protected-route redirect.
  const completeAppleSession = async (token) => {
    localStorage.setItem("gifted_token", token);
    try {
      const me = await api.get("/auth/me"); // throws (401) if the token is invalid/expired
      persist(token, me.data);
      return me.data;
    } catch (e) {
      localStorage.removeItem("gifted_token");
      throw e;
    }
  };

  const logout = () => {
    localStorage.removeItem("gifted_token");
    localStorage.removeItem("gifted_user");
    setUser(null);
  };

  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser((u) => ({ ...u, ...res.data }));
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, loginWithApple, completeAppleSession, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
